import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { 
  Copy, 
  ExternalLink, 
  QrCode, 
  Calendar, 
  Users,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  AlertTriangle,
  Globe,
  BarChart2,
  FileX,
  Ban,
  Download,
  MessageCircle,
  Clock
} from "lucide-react";
import useClinic from "../../auth/useClinic";
import useUpdateClinic from "../../clinic/useUpdateClinic";
import toast from "react-hot-toast";
import QRCode from "react-qr-code";
import { useOnlineBookings } from "../useOnlineBookings";
import OnlineBookingsTable from "../OnlineBookingsTable";
import useScrollToTop from "../../../hooks/useScrollToTop";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import DraftsTable from "../components/DraftsTable";
import BlockedNumbersManager from "../components/BlockedNumbersManager";
import useUserClinics from "../../clinic/useUserClinics";
import { setActiveClinic } from "../../../services/apiClinic";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getClinicProfileSettings, upsertClinicProfileSettings } from "../../../services/apiClinicProfile";
import WorkingHours from "../../clinic/WorkingHours";
import { getDayName, initializeAvailableTime } from "../../clinic/clinicUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function AdvancedOnlineBooking() {
  useScrollToTop();
  const { data: clinic, isLoading: isClinicLoading, isError, error } = useClinic();
  const { mutate: updateClinic, isPending: isUpdating } = useUpdateClinic();
  const { data: clinics } = useUserClinics();
  const queryClient = useQueryClient();
  
  const [clinicFormData, setClinicFormData] = useState({
    booking_price: "",
    online_booking_enabled: true,
    whatsapp_enabled: false,
    whatsapp_number: "",
    prevent_conflicts: false,
    min_time_gap: 10,
    available_time: initializeAvailableTime(null),
  });

  const { data: profileSettingsData } = useQuery({
    queryKey: ["clinic-profile-settings", clinic?.clinic_uuid],
    queryFn: () => getClinicProfileSettings(clinic?.clinic_uuid),
    enabled: !!clinic?.clinic_uuid,
    retry: false,
  });

  const [locationUrl, setLocationUrl] = useState("");
  const [showLocation, setShowLocation] = useState(false);
  
  const [showQRCode, setShowQRCode] = useState(false);
  
  const { bookings, loading: bookingsLoading, error: bookingsError } = useOnlineBookings(clinic?.clinic_uuid);
  
  useEffect(() => {
    if (!clinic) return;
    
    setClinicFormData({
      booking_price: clinic.booking_price || "",
      online_booking_enabled: clinic.online_booking_enabled !== undefined ? clinic.online_booking_enabled : true,
      whatsapp_enabled: !!clinic.whatsapp_enabled,
      whatsapp_number: clinic.whatsapp_number || "",
      prevent_conflicts: !!clinic.prevent_conflicts,
      min_time_gap: clinic.min_time_gap ?? 10,
      available_time: initializeAvailableTime(clinic.available_time),
    });
  }, [clinic]);

  useEffect(() => {
    const s = profileSettingsData?.settings;
    if (!s) return;
    setLocationUrl(s?.actions?.locationUrl || "");
    setShowLocation(!!s?.actions?.showLocation);
  }, [profileSettingsData?.settings]);
  
  const handleClinicChange = (e) => {
    const { name, value } = e.target;
    setClinicFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const toggleOnlineBooking = () => {
    const newValue = !clinicFormData.online_booking_enabled;
    setClinicFormData((prev) => ({
      ...prev,
      online_booking_enabled: newValue,
    }));
    
    updateClinic({
      ...clinicFormData,
      online_booking_enabled: newValue
    });
  };
  
  const handleUpdateClinic = (e) => {
    e.preventDefault();
    updateClinic(clinicFormData);
  };

  const handleTimeChange = (day, field, value) => {
    setClinicFormData((prev) => ({
      ...prev,
      available_time: {
        ...prev.available_time,
        [day]: { ...prev.available_time[day], [field]: value },
      },
    }));
  };

  const toggleDayOff = (day) => {
    setClinicFormData((prev) => ({
      ...prev,
      available_time: {
        ...prev.available_time,
        [day]: { ...prev.available_time[day], off: !prev.available_time[day].off },
      },
    }));
  };

  const handleSaveLocationSettings = async () => {
    const clinicId = clinic?.clinic_uuid;
    if (!clinicId) return;
    const base = profileSettingsData?.settings;
    if (!base) return;

    const next = {
      ...base,
      actions: {
        ...(base.actions || {}),
        showLocation: !!showLocation,
        locationUrl: locationUrl || "",
      },
    };

    try {
      await upsertClinicProfileSettings({ clinicId, settings: next });
      queryClient.invalidateQueries({ queryKey: ["clinic-profile-settings", clinicId] });
      toast.success("تم حفظ بيانات الموقع");
    } catch (e) {
      toast.error(e?.message || "فشل حفظ بيانات الموقع");
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("تم نسخ النص إلى الحافظة");
  };
  
  const getBookingLink = () => {
    if (!clinic) return "";
    const userId = clinic.owner_user_id;
    if (!userId) return "";
    return `${window.location.origin}/book/u/${userId}`;
  };

  const getProfileLink = () => {
    if (!clinic) return "";
    const clinicId = clinic.id || clinic.clinic_uuid;
    if (!clinicId) return "";
    return `${window.location.origin}/doctor/${clinicId}`;
  };
  
  const getEmbedCode = () => {
    const link = getBookingLink();
    if (!link) return "";
    return `<iframe src="${link}" width="100%" height="600" frameborder="0"></iframe>`;
  };

  const downloadQRCode = () => {
    const svg = document.getElementById("booking-qr-code");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width + 40;
      canvas.height = img.height + 40;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 20, 20);

      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `QR-Booking-${clinic?.name || "clinic"}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
      toast.success("تم تحميل رمز QR بنجاح");
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  const handleAcceptBooking = async (bookingId) => {
    try {
      const response = await fetch(`/api/appointments/${bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'confirmed' }),
      });
      if (!response.ok) throw new Error('Failed to accept booking');
      toast.success("تم قبول الحجز بنجاح");
    } catch (error) {
      console.error("Error accepting booking:", error);
      toast.error("فشل في قبول الحجز");
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      const response = await fetch(`/api/appointments/${bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!response.ok) throw new Error('Failed to reject booking');
      toast.success("تم رفض الحجز بنجاح");
    } catch (error) {
      console.error("Error rejecting booking:", error);
      toast.error("فشل في رفض الحجز");
    }
  };
  
  if (isClinicLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (isError || !clinic) {
    return (
      <div className="space-y-8" dir="rtl">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold">إدارة الحجز الإلكتروني المتطور</h1>
          <p className="text-sm text-muted-foreground">
            إدارة إعدادات الحجز الإلكتروني وعرض الطلبات الواردة
          </p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              خطأ في تحميل بيانات العيادة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-red-500">
                {isError ? `فشل تحميل بيانات العيادة: ${error?.message || 'خطأ غير معروف'}` : 
                 "لم نتمكن من تحميل بيانات العيادة. يرجى التأكد من أنك قمت بتسجيل الدخول وأن لديك عيادة مرتبطة بحسابك."}
              </p>
              <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
                تحديث الصفحة
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 p-3 md:p-6 bg-background min-h-screen pb-20 md:pb-6" dir="rtl">
      <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
        <div className="p-1.5 md:p-2 rounded-lg bg-primary/10 text-primary">
          <Globe className="w-5 h-5 md:w-6 md:h-6" />
        </div>
        <div style={{direction: 'rtl'}}>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">الحجز الإلكتروني المتطور</h1>
          <p className="text-xs md:text-sm text-muted-foreground">تحكم كامل في إعدادات الحجز والإحصائيات</p>
        </div>
      </div>
      
      <Tabs defaultValue="settings" className="w-full" style={{direction: 'rtl'}}>
        <TabsList className="w-full md:w-auto grid grid-cols-5 md:inline-flex h-auto p-1 bg-muted/50 mb-6" style={{direction: 'rtl'}}>
          <TabsTrigger value="settings" className="text-xs md:text-sm py-2 px-1 md:px-3 gap-1 md:gap-2">
            <span className="truncate">الإعدادات</span>
          </TabsTrigger>
          <TabsTrigger value="bookings" className="text-xs md:text-sm py-2 px-1 md:px-3 gap-1 md:gap-2 relative">
            <span className="truncate">الحجوزات</span>
            {bookings?.filter(b => b.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="absolute -top-1 -left-1 px-1 h-4 min-w-[16px] flex items-center justify-center text-[10px] rounded-full">
                    {bookings.filter(b => b.status === 'pending').length}
                </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs md:text-sm py-2 px-1 md:px-3 gap-1 md:gap-2">
            <span className="truncate">الإحصائيات</span>
          </TabsTrigger>
          <TabsTrigger value="lost-leads" className="text-xs md:text-sm py-2 px-1 md:px-3 gap-1 md:gap-2">
            <span className="truncate">غير مكتملة</span>
          </TabsTrigger>
          <TabsTrigger value="blocked" className="text-xs md:text-sm py-2 px-1 md:px-3 gap-1 md:gap-2">
            <span className="truncate">الحظر</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6">
            {(clinics || []).length > 1 && (
              <Card className="bg-card/70" style={{direction: 'rtl'}}>
                <CardContent className="p-3 md:p-6">
                  <div className="space-y-2">
                    <Label className="text-xs md:text-sm">الفرع</Label>
                    <Select
                      value={clinic?.clinic_uuid || ""}
                      onValueChange={async (v) => {
                        try {
                          await setActiveClinic(v);
                          queryClient.invalidateQueries({ queryKey: ["clinic"] });
                          queryClient.invalidateQueries({ queryKey: ["user-clinics"] });
                        } catch (e) {
                          toast.error(e?.message || "تعذر تغيير الفرع");
                        }
                      }}
                    >
                      <SelectTrigger className="h-10 md:h-11">
                        <SelectValue placeholder="اختر فرع" />
                      </SelectTrigger>
                      <SelectContent>
                        {(clinics || []).map((c) => (
                          <SelectItem key={c.clinic_uuid} value={c.clinic_uuid}>
                            {c.name || c.clinic_uuid}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Online Booking Status Card */}
            <Card className="bg-card/70" style={{direction: 'rtl'}}>
                <CardHeader className="p-3 md:p-6">
                <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base md:text-lg m-0">
                    حالة الحجز من النت
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                        checked={clinicFormData.online_booking_enabled}
                        onCheckedChange={toggleOnlineBooking}
                    />
                    <Badge 
                        variant={clinicFormData.online_booking_enabled ? "default" : "secondary"}
                        className="gap-1 text-xs whitespace-nowrap"
                    >
                        {clinicFormData.online_booking_enabled ? (
                        <>
                            <CheckCircle className="h-3 w-3 md:h-4 md:w-4" />
                            مفعل
                        </>
                        ) : (
                        <>
                            <XCircle className="h-3 w-3 md:h-4 md:w-4" />
                            متقفل
                        </>
                        )}
                    </Badge>
                    </div>
                </div>
                </CardHeader>
                <CardContent className="p-3 md:p-6 pt-0">
                <p className="text-xs md:text-sm text-muted-foreground">
                    {clinicFormData.online_booking_enabled
                    ? "الحجز من النت شغال دلوقتي"
                    : "الحجز من النت متقفل دلوقتي"}
                </p>
                </CardContent>
            </Card>
            
            {/* Booking Link Card */}
            <Card className="bg-card/70">
                <CardHeader className="p-3 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <LinkIcon className="h-4 w-4 md:h-5 md:w-5" />
                    رابط الحجز
                </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 md:space-y-4 p-3 md:p-6 pt-0">
                <div className="flex flex-col gap-2">
                    <Input
                    value={getBookingLink() || "جارٍ تحميل الرابط..."}
                    readOnly
                    dir="ltr"
                    className="flex-1 text-xs md:text-sm w-full text-left"
                    />
                    <div className="flex gap-2 w-full">
                    <Button
                        onClick={() => window.open(getProfileLink(), "_blank")}
                        variant="outline"
                        className="gap-1 md:gap-2 flex-1"
                        size="sm"
                        disabled={!getProfileLink()}
                    >
                        <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
                        <span className="text-xs md:text-sm">الملف الشخصي</span>
                    </Button>
                    <Button
                        onClick={() => copyToClipboard(getBookingLink())}
                        className="gap-1 md:gap-2 flex-1"
                        size="sm"
                        disabled={!getBookingLink()}
                    >
                        <Copy className="h-3 w-3 md:h-4 md:w-4" />
                        <span className="text-xs md:text-sm">نسخ</span>
                    </Button>
                    <Button
                        onClick={() => window.open(getBookingLink(), "_blank")}
                        variant="outline"
                        className="gap-1 md:gap-2 flex-1"
                        size="sm"
                        disabled={!getBookingLink()}
                    >
                        <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
                        <span className="text-xs md:text-sm">فتح</span>
                    </Button>
                    </div>
                </div>
                
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <Button
                    onClick={() => setShowQRCode(!showQRCode)}
                    variant="outline"
                    size="sm"
                    className="gap-1 md:gap-2 w-full sm:w-auto text-xs md:text-sm"
                    disabled={!getBookingLink()}
                    >
                    <QrCode className="h-3 w-3 md:h-4 md:w-4" />
                    {showQRCode ? "إخفاء" : "عرض"} رمز QR
                    </Button>
                    
                    <Button
                    onClick={() => copyToClipboard(getEmbedCode())}
                    variant="outline"
                    size="sm"
                    className="gap-1 md:gap-2 w-full sm:w-auto text-xs md:text-sm"
                    disabled={!getBookingLink()}
                    >
                    <Copy className="h-3 w-3 md:h-4 md:w-4" />
                    نسخ كود التضمين
                    </Button>
                </div>
                
                {showQRCode && getBookingLink() && (
                    <div className="mt-3 md:mt-4 flex flex-col items-center gap-3">
                        <div className="p-3 md:p-4 bg-white rounded-lg inline-block border shadow-sm">
                            <QRCode 
                                id="booking-qr-code"
                                value={getBookingLink()} 
                                size={120} 
                                className="w-full max-w-[120px] h-auto" 
                            />
                        </div>
                        <Button 
                            onClick={downloadQRCode}
                            variant="outline"
                            size="sm"
                            className="gap-2 text-xs"
                        >
                            <Download className="h-3 w-3" />
                            تحميل QR Code
                        </Button>
                    </div>
                )}
                
                <p className="text-[10px] md:text-xs text-muted-foreground">
                    شارك هذا الرابط مع مرضاك لحجز المواعيد، أو استخدم رمز QR في العيادة
                </p>
                </CardContent>
            </Card>
            
            <Card>
                <CardHeader className="p-3 md:p-6 pb-2">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                        <Calendar className="h-4 w-4 md:h-5 md:w-5" />
                        إعدادات الحجز
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-3 md:p-6 pt-0">
                    <form onSubmit={handleUpdateClinic} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="bookingPrice" className="text-xs md:text-sm">سعر الحجز</Label>
                            <Input
                                id="bookingPrice"
                                name="booking_price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={clinicFormData.booking_price}
                                onChange={handleClinicChange}
                                placeholder="أدخل سعر الحجز"
                                className="text-sm"
                            />
                        </div>

                        <div className="space-y-3 pt-4 border-t">
                            <Label className="text-xs md:text-sm">أوقات العمل</Label>
                            <WorkingHours
                                availableTime={clinicFormData.available_time}
                                onTimeChange={handleTimeChange}
                                onDayToggle={toggleDayOff}
                                getDayName={getDayName}
                            />
                        </div>

                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium flex items-center gap-2">
                                        <MessageCircle className="w-4 h-4 text-[#25D366]" />
                                        تفعيل زر واتساب
                                    </Label>
                                    <p className="text-xs text-muted-foreground">عرض زر واتساب للمرضى في صفحة الحجز</p>
                                </div>
                                <Switch 
                                    checked={clinicFormData.whatsapp_enabled}
                                    onCheckedChange={(checked) => setClinicFormData(prev => ({ ...prev, whatsapp_enabled: checked }))}
                                />
                            </div>
                            
                            {clinicFormData.whatsapp_enabled && (
                                <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                    <Label htmlFor="whatsappNumber" className="text-xs">رقم الواتساب</Label>
                                    <Input
                                        id="whatsappNumber"
                                        name="whatsapp_number"
                                        type="tel"
                                        value={clinicFormData.whatsapp_number}
                                        onChange={handleClinicChange}
                                        placeholder="مثال: 201234567890"
                                        className="text-sm"
                                    />
                                    <p className="text-[10px] text-muted-foreground">أدخل الرقم بالكود الدولي بدون علامة +</p>
                                </div>
                            )}
                        </div>

                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-primary" />
                                        منع تضارب المواعيد
                                    </Label>
                                    <p className="text-xs text-muted-foreground">ضمان وجود فاصل زمني بين كل حجزين</p>
                                </div>
                                <Switch 
                                    checked={clinicFormData.prevent_conflicts}
                                    onCheckedChange={(checked) => setClinicFormData(prev => ({ ...prev, prevent_conflicts: checked }))}
                                />
                            </div>
                            
                            {clinicFormData.prevent_conflicts && (
                                <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                    <Label htmlFor="minTimeGap" className="text-xs">الوقت اللازم بين كل حجز (بالدقائق)</Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            id="minTimeGap"
                                            name="min_time_gap"
                                            type="number"
                                            min="1"
                                            value={clinicFormData.min_time_gap}
                                            onChange={handleClinicChange}
                                            className="text-sm flex-1"
                                        />
                                        <span className="text-xs text-muted-foreground">دقيقة</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-primary" />
                                        إظهار الموقع
                                    </Label>
                                    <p className="text-xs text-muted-foreground">إظهار زر الموقع في صفحة نجاح الحجز</p>
                                </div>
                                <Switch checked={showLocation} onCheckedChange={setShowLocation} />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="locationUrl" className="text-xs">رابط Google Maps</Label>
                                <Input
                                    id="locationUrl"
                                    value={locationUrl}
                                    onChange={(e) => setLocationUrl(e.target.value)}
                                    placeholder="https://maps.app.goo.gl/..."
                                    className="text-sm"
                                />
                            </div>

                            <Button type="button" variant="outline" size="sm" className="w-full" onClick={handleSaveLocationSettings}>
                                حفظ بيانات الموقع
                            </Button>
                        </div>
                        
                        <Button type="submit" disabled={isUpdating} size="sm" className="w-full">
                            {isUpdating ? "جاري الحفظ..." : "حفظ الإعدادات"}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="bookings">
            <Card>
                <CardHeader className="p-3 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="h-4 w-4 md:h-5 md:w-5" />
                    طلبات الحجز القادمة
                </CardTitle>
                </CardHeader>
                <CardContent className="p-0 md:p-6 md:pt-0">
                {bookingsLoading ? (
                    <div className="space-y-3 p-3 md:p-0">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                    ))}
                    </div>
                ) : bookingsError ? (
                    <div className="text-center py-8 text-red-500 px-3">
                    <AlertTriangle className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-4" />
                    <h3 className="text-base md:text-lg font-medium mb-2">خطأ في تحميل الحجوزات</h3>
                    <p className="text-xs md:text-sm">{bookingsError}</p>
                    </div>
                ) : (
                    <OnlineBookingsTable
                    appointments={bookings}
                    onAccept={handleAcceptBooking}
                    onReject={handleRejectBooking}
                    />
                )}
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="analytics">
            <AnalyticsDashboard clinicId={clinic?.clinic_uuid} />
        </TabsContent>

        <TabsContent value="lost-leads">
            <DraftsTable clinicId={clinic?.clinic_uuid} />
        </TabsContent>

        <TabsContent value="blocked">
            <BlockedNumbersManager clinicId={clinic?.clinic_uuid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
